// app/frontend/pages/Videos/VideoIndex.tsx
import { useState, useMemo, useEffect } from "react";
import { Grid, Stack, Box } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { usePage } from "@inertiajs/react";
import { L2KH3, L2KText, L2KCaption } from "../../components/ui/Typography";
import VideoCard from "../../components/videos/VideoCard";
import HybridLayout from "../../components/layouts/HybridLayout";
import PageContainer from "../../components/layouts/PageContainer";
import { ComingSoonPageOverlay } from "../../components/videos/ComingSoonPageOverlay";
import FilterDrawer, {
  FilterState,
} from "../../components/ui/Drawers/FilterDrawer";
import ModernPagination from "../../components/ui/Pagination";
import { SearchFilterBar } from "../../components/common/SearchFilterBar";
import { SEO } from "../../components/common/SEO";

import type {
  Video,
  LearningTrack,
  UserVideo,
  Category,
} from "../../types/video";

interface Props {
  videos: Video[];
  learning_tracks: LearningTrack[];
  user_videos: Record<number, UserVideo>;
  filters: {
    categories: Category[];
    skill_levels: string[];
    tags: string[];
  };
}

export default function VideosIndex({
  videos,
  learning_tracks,
  user_videos,
  filters: filterOptions,
}: Props) {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    selectedCategory: null,
    selectedTrack: null,
    selectedCategories: [],
    selectedSkillLevels: [],
    selectedTags: [],
    selectedTracks: [],
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters
  const filteredVideos = useMemo(() => {
    let result = videos;

    // Track filter
    if (filters.selectedTracks.length > 0) {
      const trackVideoIds = filters.selectedTracks.flatMap((trackId) => {
        const track = learning_tracks.find((t) => t.id === trackId);
        return track ? track.video_ids : [];
      });
      result = result.filter((v) => trackVideoIds.includes(v.id));
    }

    // Category filter (video has ANY of the selected categories)
    if (filters.selectedCategories.length > 0) {
      result = result.filter((v) =>
        v.categories.some((cat) => filters.selectedCategories.includes(cat.id))
      );
    }

    // Skill level filter
    if (filters.selectedSkillLevels.length > 0) {
      result = result.filter((v) =>
        filters.selectedSkillLevels.includes(v.skill_level)
      );
    }

    // Tags filter
    if (filters.selectedTags.length > 0) {
      result = result.filter((v) =>
        filters.selectedTags.some((t) => v.tags.includes(t))
      );
    }

    // Search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          v.categories.some((cat) => cat.name.toLowerCase().includes(q))
      );
    }

    return result;
  }, [videos, learning_tracks, filters, debouncedSearch]);

  // Paginated videos
  const paginatedVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredVideos.slice(startIndex, endIndex);
  }, [filteredVideos, currentPage]);

  const totalPages = Math.ceil(filteredVideos.length / ITEMS_PER_PAGE);

  // Reset to first page when filters change
  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Active filters count for the badge on the button
  const activeFilterCount =
    filters.selectedCategories.length +
    filters.selectedSkillLevels.length +
    filters.selectedTags.length +
    filters.selectedTracks.length;

  // Selected track for header
  const selectedTrack =
    filters.selectedTracks.length === 1
      ? learning_tracks.find((t) => t.id === filters.selectedTracks[0]) || null
      : null;

  const handleFavoriteToggle = async (videoId: number) => {
    try {
      await fetch(`/videos/${videoId}/toggle_favorite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      // Optionally refresh or update local state
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleBookmarkToggle = async (videoId: number) => {
    try {
      await fetch(`/videos/${videoId}/toggle_bookmark`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      // Optionally refresh or update local state
    } catch (error) {
      console.error("Failed to toggle bookmark:", error);
    }
  };

  const handleVideoClick = (video: Video) => {
    // Include track context in URL if a single track is selected (for breadcrumbs)
    const trackParam = selectedTrack ? `?track_id=${selectedTrack.id}` : "";
    window.location.href = `/videos/${video.slug}${trackParam}`;
  };

  const { coming_soon } = usePage().props as { coming_soon?: boolean };

  const pageBody = (
    <PageContainer size="lg">
        <Stack gap="8px" style={{ overflow: "visible" }}>
        {/* Page header */}
        <Box>
          <L2KH3 mb={0}>
            {selectedTrack ? selectedTrack.name : "All Videos"}
          </L2KH3>
          {selectedTrack && (
            <L2KText variant="secondary" mb="md">
              {selectedTrack.description}
            </L2KText>
          )}
          <L2KCaption variant="tertiary">
            {(() => {
              const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
              const endItem = Math.min(currentPage * ITEMS_PER_PAGE, filteredVideos.length);
              if (startItem === endItem) {
                return `Showing ${startItem} of ${filteredVideos.length} videos`;
              }
              return `Showing ${startItem}-${endItem} of ${filteredVideos.length} videos`;
            })()}
          </L2KCaption>
        </Box>

        {/* Search + Filters Bar */}
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="Search videos..."
          onFilterClick={toggleDrawer}
          activeFilterCount={activeFilterCount}
          debouncing={searchTerm !== debouncedSearch}
        />

        {/* Pagination - Top */}
        {totalPages > 1 && (
          <ModernPagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            totalItems={filteredVideos.length}
            itemsPerPage={ITEMS_PER_PAGE}
            showItemsInfo={false}
            size="md"
            style={{ paddingTop: 0 }}
          />
        )}

        {/* Grid */}
        <Grid>
          {paginatedVideos.map((video) => (
            <Grid.Col key={video.id} span={{ base: 12, xs: 6, sm: 4, md: 4, lg: 3, xl: 3 }}>
              <VideoCard
                video={video}
                userVideo={user_videos[video.id] || null}
                onFavoriteToggle={handleFavoriteToggle}
                onBookmarkToggle={handleBookmarkToggle}
                onVideoClick={handleVideoClick}
              />
            </Grid.Col>
          ))}
        </Grid>

        {filteredVideos.length === 0 && (
          <Box ta="center" py="xl">
            <L2KH3 c="#B8B8B8" mb="md">
              No videos found
            </L2KH3>
            <L2KText variant="tertiary">
              Try adjusting your filters or search term
            </L2KText>
          </Box>
        )}

        {/* Pagination - Bottom */}
        {totalPages > 1 && (
          <ModernPagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            totalItems={filteredVideos.length}
            itemsPerPage={ITEMS_PER_PAGE}
            showItemsInfo={true}
            size="md"
          />
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          opened={drawerOpened}
          onClose={closeDrawer}
          videos={videos}
          learningTracks={learning_tracks}
          categories={filterOptions.categories}
          currentFilters={filters}
          onApply={(next) => {
            handleFiltersChange(next);
            closeDrawer();
          }}
        />
        </Stack>
      </PageContainer>
  );

  return (
    <>
      <SEO
        title={
          selectedTrack
            ? `${selectedTrack.name} - Videos`
            : "Videos - Learntokiz"
        }
        description={
          selectedTrack
            ? selectedTrack.description
            : "Browse our collection of urbankiz dance videos and learning tracks"
        }
      />
      {coming_soon ? (
        <ComingSoonPageOverlay>{pageBody}</ComingSoonPageOverlay>
      ) : (
        pageBody
      )}
    </>
  );
}

// Per-page layout (Inertia pattern)
VideosIndex.layout = (page: React.ReactNode) => (
  <HybridLayout hideOnScroll={true}>{page}</HybridLayout>
);
